import { summariseMrr, type MrrSummary } from 'src/secret-menu/billing/billingUtils';
import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';

type RevenueCoverage = {
  /** Units belonging to firms that gave us a client count - the only ones the ratio can use. */
  units: number;
  activeUnits: number;
  stated: number;
  firmsWithStated: number;
  firmsWithoutStated: number;
};

export type RevenueModel = {
  /** Billing row per firm, keyed on the company name the usage rows are reported under. */
  byFirm: Map<string, BillingDetail>;
  summary: MrrSummary;
  /** Monthly bill if every firm loaded every client it says it has. */
  potentialPence: number;
  coverage: RevenueCoverage;
  /** Firms on the dashboard with no billing row, or a row with nothing configured. */
  unbilledFirms: string[];
  /** Companies that are billed but produced no usage at all, so contribute nothing here. */
  billedWithoutUsage: BillingDetail[];
};

/**
 * Both sides key on Company.name — usage rows are built with `company=company.name` and
 * billing rows expose the same field as `companyName` — so the join is exact, not fuzzy.
 *
 * Totals cover only firms present in the usage data, which keeps revenue consistent with the
 * usage figures beside it. Billed companies with no usage are reported separately rather than
 * folded in silently, because that difference is the reason this total can trail the billing tab.
 */
export function buildRevenueModel(
  details: readonly BillingDetail[] | undefined,
  firmNames: readonly string[],
  showInternal = false,
): RevenueModel | null {
  if (!details) return null;

  const names = new Set(firmNames);
  const byFirm = new Map<string, BillingDetail>();
  const billedWithoutUsage: BillingDetail[] = [];

  for (const detail of details) {
    if (!showInternal && detail.isInternal) continue;

    if (names.has(detail.companyName)) {
      byFirm.set(detail.companyName, detail);
    } else if ((detail.monthlyBillPence ?? 0) > 0) {
      billedWithoutUsage.push(detail);
    }
  }

  const unbilledFirms = firmNames.filter(name => !(byFirm.get(name)?.monthlyBillPence ?? 0));
  const matched = [...byFirm.values()];

  const coverage: RevenueCoverage = {
    units: 0,
    activeUnits: 0,
    stated: 0,
    firmsWithStated: 0,
    firmsWithoutStated: 0,
  };
  let potentialPence = 0;

  for (const row of matched) {
    // A firm that never told us a number cannot move, so it contributes its current bill
    // rather than nothing - otherwise adding a stated count would make the total fall.
    potentialPence += row.potentialMonthlyBillPence ?? row.monthlyBillPence ?? 0;

    if (row.statedClientCount === null || row.statedClientCount <= 0) {
      coverage.firmsWithoutStated += 1;
      continue;
    }

    coverage.firmsWithStated += 1;
    coverage.stated += row.statedClientCount;
    coverage.units += row.billableUnitCount;
    coverage.activeUnits += row.activeBillableUnitCount;
  }

  return {
    byFirm,
    summary: summariseMrr(matched, showInternal),
    potentialPence,
    coverage,
    unbilledFirms,
    billedWithoutUsage,
  };
}

/** Annual run rate. Billing is monthly, so this is simply twelve months of it. */
export function toArrPence(monthlyPence: number | null): number | null {
  return monthlyPence === null ? null : monthlyPence * 12;
}

/**
 * How much of a firm's stated book is actually on the platform. Units are families plus
 * family-less clients, so this is a coverage indicator rather than an exact client ratio -
 * a firm whose clients are mostly in couples lands well under 100% while being fully loaded.
 */
export function percentOfStated(count: number, stated: number | null): number | null {
  if (stated === null || stated <= 0) return null;
  return (count / stated) * 100;
}

export function formatPercent(value: number | null): string {
  return value === null ? '—' : `${Math.round(value)}%`;
}

export function billingFor(
  revenue: RevenueModel | null,
  firmName: string,
): BillingDetail | undefined {
  return revenue?.byFirm.get(firmName);
}
